import { useEffect, useMemo, useState } from "react"
import { GetAccumulateAmount, GetCurrentAmount, GetDataset, GetLabels, GetPorcentage } from "src/components/dashboard/PanelVentas/functions"
import { CombineMonthlySales, CompanyLessAnnualSales, CompanyMostAnnualSales, MonthLowestSales, MonthMostSales, ensureTwelveMonths } from "./utils/functions"
import moment from "moment"
import { Checkbox, Popup } from "semantic-ui-react"
import ChartCardHolding from "../Chart/ChartCardHolding"
import { InfoCard } from "./utils/InfoCard"
import FeatherIcon from "feather-icons-react"
import { GetCompanyData } from "src/graphql/company"
import { ResumeMonthCard } from "./utils/VentasComponents/ResumeMonthCard"
import LoadingSpinner from "../../../../../general/js/spinnerLoading"

const currentMonth = moment().month()
const currentMonthName = moment().format('MMMM')
const currentYear = moment().year()
const previousYear = moment().year() - 1

export const VentasNetas = ({ loading, source, lastYear, period, setPeriod, specificPeriods, setSpecificPeriods, isSale = true }) => {

  const [hidden, setHidden] = useState('')
  const [mostSalesAnnualCompany, setMostSalesAnnualCompany] = useState('')
  const [lessSalesAnnualCompany, setLessSalesAnnualCompany] = useState('')
  const { data: companyMostData } = GetCompanyData(mostSalesAnnualCompany)
  const { data: companyLessData } = GetCompanyData(lessSalesAnnualCompany)

  const semestrePeriods = [
    { label: '1er Semestre', value: 'firstSemester' },
    { label: '2do Semestre', value: 'secondSemester' }
  ]

  const quarterPeriods = [
    { label: '1er Trimestre', value: 'firstQuarter' },
    { label: '2do Trimestre', value: 'secondQuarter' },
    { label: '3er Trimestre', value: 'thirdQuarter' },
    { label: '4to Trimestre', value: 'fourthQuarter' }
  ]

  const labels = GetLabels({ source, period, specificPeriods })

  const data = useMemo(() => {
    if (!source) return
    const combinedSource = ensureTwelveMonths(CombineMonthlySales(source), currentYear)
    const combinedLastYear = ensureTwelveMonths(
      CombineMonthlySales(Array.isArray(lastYear) ? lastYear : []),
      previousYear,
    )
    return [
      GetDataset({ source: combinedLastYear, period, specificPeriods, isPast: true, hidden: hidden === 'past' }),
      GetDataset({ source: combinedSource, period, specificPeriods, isPast: false, hidden: hidden === 'present' }),
    ]
  }, [period, specificPeriods, source, lastYear, hidden])

  useEffect(() => {
    const periodHandlers = {
      semester: () => {
        setSpecificPeriods(currentMonth <= 5 ? 'firstSemester' : 'secondSemester')
      },
      quarter: () => {
        if (currentMonth <= 2) return setSpecificPeriods('firstQuarter')
        if (currentMonth <= 5) return setSpecificPeriods('secondQuarter')
        if (currentMonth <= 8) return setSpecificPeriods('thirdQuarter')
        setSpecificPeriods('fourthQuarter')
      }
    }

    if (periodHandlers[period]) {
      periodHandlers[period]()
    }
  }, [period])


  const {
    currentMonthSales = {},
    accActualSales = {},
    accPastSales = {},
    porcentage = 0,
    footer = '',
    mostMonthSales = '',
    lowestMonthSales = ''
  } = useMemo(() => {
    if (!source) return {}

    const combinedSource = CombineMonthlySales(source)
    const currentMonthData = GetCurrentAmount({ source: combinedSource, isPast: false })
    const accActualData = GetAccumulateAmount({ source: data?.[1], period, specificPeriods, isPast: false })
    const accPastData = GetAccumulateAmount({ source: data?.[0], period, specificPeriods, isPast: true })
    const porcentageData = GetPorcentage({ data })
    const mostSalesCompany = CompanyMostAnnualSales(source)
    const lestSalesCompany = CompanyLessAnnualSales(source)

    const mostMonthSales = MonthMostSales(combinedSource);
    const monthLowestSales = MonthLowestSales(combinedSource);

    setMostSalesAnnualCompany(mostSalesCompany)
    setLessSalesAnnualCompany(lestSalesCompany.companyId)

    return {
      currentMonthSales: currentMonthData?.content || {},
      accActualSales: accActualData?.content || {},
      accPastSales: accPastData?.content || {},
      porcentage: porcentageData.porcentage || 0,
      footer: porcentageData.footer || '',
      mostMonthSales: mostMonthSales.month,
      lowestMonthSales: monthLowestSales.month,
    }
  }, [source, data, period, specificPeriods])

  const companyName = companyMostData?.Company?.alias || companyMostData?.Company?.name || ''
  const companyLessName = companyLessData?.Company?.alias || companyLessData?.Company?.name || ''

  const CardInfo = [
    {
      id: 'currentMonth',
      name: currentMonthName,
      subName: currentYear,
      amount: currentMonthSales,
      classBG: 'tw-bg-gray-100 tw-border-solid tw-border-[1px] tw-border-gray-200',
      classTitle: 'tw-text-gray-600 tw-capitalize',
      classAmount: 'tw-text-black'
    },
    {
      id: 'pastTotalPeriod',
      name: 'Total Periodo',
      subName: previousYear,
      amount: accPastSales,
      classBG: 'tw-bg-purple-500',
      classTitle: 'tw-text-white tw-capitalize',
      classAmount: 'tw-text-white'
    },
    {
      id: 'actualTotalPeriod',
      name: 'Total Periodo',
      subName: currentYear,
      amount: accActualSales,
      classBG: 'tw-bg-[#0099FF]',
      classTitle: 'tw-text-white tw-capitalize',
      classAmount: 'tw-text-white'
    },
    {
      id: 'porcentageVariation',
      name: 'Variación Porcentual',
      subName: (
        <Popup
          inverted
          trigger={<FeatherIcon icon="info" size={17} className="tw-cursor-pointer" />}
          content={footer}
          position="top left"
          className="!tw-text-sm"
        />
      ),
      amount: `${porcentage}%`,
      classBG: 'tw-bg-gray-100 tw-border-solid tw-border-[1px] tw-border-gray-200',
      classTitle: 'tw-text-gray-600 tw-capitalize',
      classAmount: 'tw-text-black',
      classDiv: 'tw-ms-auto'
    },
  ]

  const CardInfo2 = [
    {
      id: 'annualMostSales',
      name: 'Empresa con mayor monto de ventas',
      amount: companyName,
      subAmount: currentYear,
      classBG: 'tw-bg-gray-100 tw-border-solid tw-border-[1px] tw-border-gray-200',
      classTitle: 'tw-text-gray-600',
      classAmount: 'tw-text-black',
      classSubAmount: 'tw-text-black'
    },
    {
      id: 'annulLessSales',
      name: 'Empresa con menor monto de ventas',
      amount: companyLessName,
      subAmount: currentYear,
      classBG: 'tw-bg-gray-100 tw-border-solid tw-border-[1px] tw-border-gray-200',
      classTitle: 'tw-text-gray-600',
      classAmount: 'tw-text-black',
      classSubAmount: 'tw-text-black'
    },
    {
      id: 'monthMostSales',
      name: 'Mes con mayor monto de ventas',
      amount: moment(mostMonthSales).add(10,"hours").format('MMMM'),
      subAmount: currentYear,
      classBG: 'tw-bg-gray-100 tw-border-solid tw-border-[1px] tw-border-gray-200',
      classTitle: 'tw-text-gray-600',
      classAmount: 'tw-text-black tw-capitalize',
    },
    {
      id: 'monthLowestSales',
      name: 'Mes con menor monto de ventas',
      amount: moment(lowestMonthSales).add(10,"hours").format('MMMM'),
      subAmount: currentYear,
      classBG: 'tw-bg-gray-100 tw-border-solid tw-border-[1px] tw-border-gray-200',
      classTitle: 'tw-text-gray-600',
      classAmount: 'tw-text-black tw-capitalize',
    },
  ]

  const groupDataByCompany = (data) => {
    if (!data) return [];

    const grouped = {};

    data.forEach(item => {
      if (!grouped[item.companyId]) {
        grouped[item.companyId] = [];
      }
      grouped[item.companyId].push(item);
    });

    return Object.entries(grouped).map(([companyId, months]) => {
      const sortedMonths = [...months].sort((a, b) =>
        new Date(a.month) - new Date(b.month)
      );

      return {
        companyId,
        months: sortedMonths
      };
    });
  };

  const companiesData = groupDataByCompany(source);

  return (
    <>
      {source ? (
        <>
          <div className="tw-flex tw-flex-col tw-gap-6 tw-w-full">
            <div className="tw-flex-row tw-bg-white tw-rounded-md tw-p-7">
              <div>
                <p className="tw-font-medium tw-text-xl tw-text-black">Resumen consolidado de ventas netas</p>
              </div>
              <div className="tw-flex tw-gap-6 tw-mt-8">
                {CardInfo.map((item, index) => (
                  <InfoCard
                    key={index}
                    name={item.name}
                    subName={item.subName}
                    amount={item.amount}
                    classBG={item.classBG}
                    classTitle={item.classTitle}
                    classAmount={item.classAmount}
                    classDiv={item.classDiv}
                  />
                ))}
              </div>
              <div className="tw-w-full tw-mt-6">
                <ChartCardHolding
                  labels={labels}
                  data={data}
                  loading={loading}
                  setPeriod={setPeriod}
                  period={period}
                  setHidden={setHidden}
                  isSale={isSale}
                />
                {period !== 'year' && (
                  <div className="d-flex align-items-center justify-content-center gap-4">
                    {(period === 'semester' ? semestrePeriods : quarterPeriods).map((item, index) => (
                      <Checkbox
                        key={index}
                        radio
                        label={item.label}
                        value={item.value}
                        onChange={(e, { value }) => {
                          const [first, second] = item.label.split(' ')
                          setSpecificPeriods(value)
                        }}
                        className="checkboxRadioGroup font-weight-bold"
                        checked={specificPeriods === item.value}
                      >
                        {item.label}
                      </Checkbox>
                    ))}
                  </div>
                )}
              </div>

              <div className={`tw-flex tw-gap-6 ${period !== 'year' ? 'tw-mt-7' : 'tw-mt-[49px]'}`}>
                {CardInfo2.map((item, index) => (
                  <InfoCard
                    key={index}
                    name={item.name}
                    subName={item.subName}
                    amount={item.amount}
                    classBG={item.classBG}
                    classTitle={item.classTitle}
                    classAmount={item.classAmount}
                    classDiv={item.classDiv}
                    subAmount={item.subAmount}
                    classSubAmount={item.classSubAmount}
                  />
                ))}
              </div>
            </div>
            <div className="tw-bg-white tw-rounded-md tw-p-7 tw-flex tw-flex-col tw-gap-10">
              <div>
                <p className="tw-font-medium tw-text-xl tw-text-black">Resumen de ventas netas por empresa</p>
              </div>
              <div className="tw-flex tw-flex-col tw-gap-6">
                {companiesData.map((company, index) => (
                  <ResumeMonthCard
                    key={company.companyId}
                    source={company.months}
                    companyId={company.companyId}
                    isSale={true}
                  />
                ))}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div class="tw-bg-white tw-rounded-md tw-p-6">
            <div className="tw-flex tw-gap-6 tw-h-[13vh]">
              <div className="tw-w-1/4 tw-bg-gray-300 tw-animate-pulse tw-rounded-md"></div>
              <div className="tw-w-1/4 tw-bg-gray-300 tw-animate-pulse tw-rounded-md"></div>
              <div className="tw-w-1/4 tw-bg-gray-300 tw-animate-pulse tw-rounded-md"></div>
              <div className="tw-w-1/4 tw-bg-gray-300 tw-animate-pulse tw-rounded-md"></div>
            </div>
          <div class="tw-flex tw-items-end tw-gap-6 tw-w-full tw-h-[43vh]">
            <div class="tw-w-1/12 tw-bg-gray-300 tw-animate-pulse tw-rounded-t tw-h-[20vh]"></div>
            <div class="tw-w-1/12 tw-bg-gray-300 tw-animate-pulse tw-rounded-t tw-h-[25vh]"></div>
            <div class="tw-w-1/12 tw-bg-gray-300 tw-animate-pulse tw-rounded-t tw-h-[35vh]"></div>
            <div class="tw-w-1/12 tw-bg-gray-300 tw-animate-pulse tw-rounded-t tw-h-[32vh]"></div>
            <div class="tw-w-1/12 tw-bg-gray-300 tw-animate-pulse tw-rounded-t tw-h-[23vh]"></div>
            <div class="tw-w-1/12 tw-bg-gray-300 tw-animate-pulse tw-rounded-t tw-h-[30vh]"></div>
            <div class="tw-w-1/12 tw-bg-gray-300 tw-animate-pulse tw-rounded-t tw-h-[21vh]"></div>
            <div class="tw-w-1/12 tw-bg-gray-300 tw-animate-pulse tw-rounded-t tw-h-[18vh]"></div>
            <div class="tw-w-1/12 tw-bg-gray-300 tw-animate-pulse tw-rounded-t tw-h-[24vh]"></div>
            <div class="tw-w-1/12 tw-bg-gray-300 tw-animate-pulse tw-rounded-t tw-h-[27vh]"></div>
            <div class="tw-w-1/12 tw-bg-gray-300 tw-animate-pulse tw-rounded-t tw-h-[10vh]"></div>
            <div class="tw-w-1/12 tw-bg-gray-300 tw-animate-pulse tw-rounded-t tw-h-[29vh]"></div>
            <div class="tw-w-1/12 tw-bg-gray-300 tw-animate-pulse tw-rounded-t tw-h-[16vh]"></div>
          </div>
        </div>
      )}
    </>
  )
}